import React from 'react';
import { CountryCode, SubscriptionPlan } from '@dayetopia/types';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import { TypeOfPurchase } from '@components/shared/PurchaseOptions';
import useDesktop from '@components/shared/useDesktop';
import { IntervalUnit, ONE_MONTH_INTERVAL_LENGTH, ProductCode, THREE_MONTH_INTERVAL_LENGTH } from '@contracts';
import { useCountry } from '@hooks/useCountry';
import SelectedRadio from '@images/selected-radio.svg';
import UnselectedRadio from '@images/unselected-radio.svg';
import { showInCountryCondition } from '@utils/country';
import { getDiscountPercent } from '@utils/subscriptions';
import DiscountedPriceDisplay from '../DiscountedPriceDisplay';
import { StyledSimpleBoxWrapper } from '../SharedStyles';

interface Props {
  subscriptions: SubscriptionPlan[];
  productCode: ProductCode;
  selectedSubscription: SubscriptionPlan | null;
  onOptionSelect: (plan: SubscriptionPlan) => void;
  textColor: string;
}

const OptionRows: React.FC<Props> = ({ subscriptions, productCode, selectedSubscription, onOptionSelect, textColor }) => {
  const { t } = useTranslation();
  const { country } = useCountry();
  const isMobile = !useDesktop(1024);

  const renderOptionText = (subscription: SubscriptionPlan) => {
    if (
      subscription.subscriptionInterval.length === subscription.billing.installments &&
      subscription.subscriptionInterval.length === ONE_MONTH_INTERVAL_LENGTH
    ) {
      return t('components.subscription.monthly-plan');
    }

    if (productCode === ProductCode.ScreeningKit) {
      return t('components.subscription-options.tests-per-year', { count: subscription.delivery.installments });
    }

    if (subscription.subscriptionInterval.length === THREE_MONTH_INTERVAL_LENGTH) {
      return t('components.subscription.quarterly-plan');
    }

    if (isMobile) {
      return `${subscription.subscriptionInterval.length}-${t('components.month-subscription-short')}`;
    }

    return `${subscription.subscriptionInterval.length}-${t('components.month-subscription')}`;
  };

  const renderPerText = () => {
    if (productCode === ProductCode.ScreeningKit) {
      return t('components.subscription-options.per-test');
    }
    return '';
  };

  const getSubTexts = (subscription: SubscriptionPlan) => {
    const subTexts = [];

    if (subscription.subscriptionInterval.unit === IntervalUnit.Months) {
      if (subscription.subscriptionInterval.length === ONE_MONTH_INTERVAL_LENGTH) {
        const monthlySubtext =
          productCode === ProductCode.Proviotics ? t('components.month-subscription-proviotics-subtext') : t('components.month-subscription-subtext');
        subTexts.push(monthlySubtext);
      } else if (subscription.subscriptionInterval.length === THREE_MONTH_INTERVAL_LENGTH) {
        const quarterSubtext =
          productCode === ProductCode.Proviotics
            ? t('components.quarter-subscription-proviotics-subtext')
            : t('components.quarter-subscription-subtext');
        subTexts.push(quarterSubtext);
      }
    }

    if (subscription?.delivery?.isFree) {
      subTexts.push(t('components.free-shipping-perk'));
    }

    if (productCode === ProductCode.Proviotics) {
      if (subscription.subscriptionInterval.length === ONE_MONTH_INTERVAL_LENGTH && country === CountryCode.GB) {
        subTexts.push(t('components.free-shipping-perk'));
      }
      if (subscription.subscriptionInterval.length === THREE_MONTH_INTERVAL_LENGTH) {
        subTexts.push(t('components.free-storage-tin'));
      }
    }

    subTexts.push(t('components.pause-or-cancel-anytime'));

    if (productCode === ProductCode.Pads || productCode === ProductCode.Proviotics) {
      if (subscription.subscriptionInterval.unit === IntervalUnit.Months) {
        if (subscription.subscriptionInterval.length === THREE_MONTH_INTERVAL_LENGTH) {
          subTexts.push(t('components.reduced-carbon-emissions-option'));
        }
      }
    }

    if (productCode === ProductCode.TamponBox) {
      if (subscription?.freeItem?.key === 'free-case') {
        subTexts.push(t(`components.subscription-options.free-case-and-pouch`));
      } else if (subscription?.freeItem?.key) {
        const text = t(`components.subscription-options.${subscription?.freeItem?.key}`).replace('+ ', '');

        if (text) {
          subTexts.push(text);
        }
      }
    }

    return subTexts;
  };

  const renderPurchaseDescription = (subscription: SubscriptionPlan) => {
    const subTexts = getSubTexts(subscription);

    if (subTexts.length === 0) {
      return null;
    }

    return (
      <>
        {subTexts.map((subText) => {
          return (
            <PurchaseDescriptionRow key={subText}>
              <Dot />
              <Subtext>{subText}</Subtext>
            </PurchaseDescriptionRow>
          );
        })}
      </>
    );
  };

  return (
    <OptionRowsWrap id="multi-month-option-rows" className={`product-${productCode}`}>
      {subscriptions.map((subscription) => {
        if (!showInCountryCondition(subscription.showInCountries, country)) {
          return null;
        }

        const isSelected =
          selectedSubscription &&
          selectedSubscription.planCode === subscription.planCode &&
          selectedSubscription.delivery.installments === subscription.delivery.installments;
        const savePercent = Math.round(getDiscountPercent(subscription, country));
        const subscriptionPrice = subscription.discountedPrice;
        const bottomDescription = renderPurchaseDescription(subscription);
        const price = subscriptionPrice || subscription?.basePrice;
        const perText = renderPerText();
        const subscriptionInterval = subscription?.subscriptionInterval?.length || 1;
        const originalPrice = (subscriptionInterval * (subscription?.basePrice || 0)).toFixed(2);
        const finalPrice = (subscriptionInterval * (price || 0)).toFixed(2);

        if (!price) {
          return null;
        }

        return (
          <OptionRow
            $isSelected={!!isSelected}
            $selectable
            $isRadioSelection
            key={`${subscription.planCode}-${subscription.delivery.installments}`}
            onClick={() => {
              onOptionSelect(subscription);
            }}
          >
            <PurchaseOptionTextWrap>
              <RadioIndicatorWrap>
                <input hidden type="radio" value={TypeOfPurchase.SubscriptionPurchase} />
                <RadioIndicator $textColor={textColor}>{isSelected ? <SelectedRadio /> : <UnselectedRadio />}</RadioIndicator>
              </RadioIndicatorWrap>
              <PurchaseDescription>
                <PurchaseDescriptionTopRow>
                  <OptionDescription>{renderOptionText(subscription)}</OptionDescription>
                </PurchaseDescriptionTopRow>
              </PurchaseDescription>
              <OptionsPrice color="transparent">
                <PriceFrame>
                  <DiscountedPriceDisplay originalPrice={originalPrice} price={finalPrice} />
                </PriceFrame>
                {perText ? <Span style={{ marginTop: 2 }}>{perText}</Span> : null}
              </OptionsPrice>
            </PurchaseOptionTextWrap>
            {bottomDescription ? <BottomDescription>{bottomDescription}</BottomDescription> : null}
            {savePercent > 0 && (
              <DiscountBox data-testid="discount-box" data-percent={savePercent}>
                {t('components.subscription-options.save', {
                  percent: savePercent
                })}
              </DiscountBox>
            )}
          </OptionRow>
        );
      })}
    </OptionRowsWrap>
  );
};

export default OptionRows;

const OptionRowsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: flex-start;

  &.product-tampon_box .benefits-wrap {
    height: 38px;
  }
`;

const OptionRow = styled(StyledSimpleBoxWrapper)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  position: relative;
`;

const BottomDescription = styled.div``;

const DiscountBox = styled.div`
  position: absolute;
  right: 16px;
  top: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${Colors.lilac};
  color: ${Colors.cream};
  border-radius: 2px;
  padding: 2px 5px;
  display: inline-block;
  text-transform: uppercase;
  font-family: BrownStd;
  font-size: 10px;
  font-style: normal;
  font-weight: 700;
  line-height: 11px;
  letter-spacing: 0.5px;
`;

const Span = styled.span`
  opacity: 0.5;
  color: inherit;
  font-family: BrownStd;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 100%;
  letter-spacing: 0;
  text-align: right;
  white-space: nowrap;
`;

const PurchaseDescription = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
`;

const PurchaseDescriptionRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 5px;
  align-items: center;

  & span {
    display: flex;
    color: ${Colors.gold};
  }
`;

const PurchaseDescriptionTopRow = styled(PurchaseDescriptionRow)`
  @media (max-width: 400px) {
    align-items: flex-start;
    gap: 7px;
    margin-bottom: 5px;

    & > div {
      line-height: 1;
    }
  }
`;

const OptionDescription = styled.div`
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 26px;
  letter-spacing: 0;
  text-align: left;
  @media (max-width: 1024px) {
    font-size: 13px;
    line-height: 150%;
  }
`;

const Subtext = styled.p`
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 140%;
  color: inherit;
`;

const Dot = styled.div`
  background: ${Colors.gold};
  width: 4px;
  height: 4px;
  border-radius: 50%;
`;

const RadioIndicatorWrap = styled.div``;

const RadioIndicator = styled.div<{ $textColor: string }>`
  width: 20px;
  height: 20px;

  svg circle:first-child {
    stroke: ${({ $textColor }) => $textColor};
    fill: #0000 !important;
  }
  svg circle:last-child {
    fill: ${({ $textColor }) => $textColor};
  }
`;

const PurchaseOptionTextWrap = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
`;

const PriceFrame = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: flex-start;
  padding: 0;
  flex: none;
  flex-grow: 0;
  font-family: 'Domaine';
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 110%;
  text-align: center;
`;

const OptionsPrice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: 0;

  & > div {
    font-family: 'Domaine';
    font-style: normal;
    font-weight: normal;
    font-size: 18px;
    line-height: 110%;
  }

  & > span {
    font-family: 'BrownStd';
    font-style: normal;
    font-weight: 400;
    font-size: 10px;
    line-height: 100%;
    opacity: 0.5;
  }
`;
